import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, XCircle, Clock, Eye, Image, Link2, 
  ChevronDown, ChevronUp, Loader2, AlertTriangle,
  Filter, RefreshCw, CheckCheck, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { API } from '../constants';

const VerificationQueue = () => {
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState({ pending_count: 0, approved_today: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  const fetchVerifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/admin/verifications?status=${statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.verifications) {
        setVerifications(data.verifications);
        setStats(data.stats || { pending_count: 0, approved_today: 0 });
      }
    } catch (err) {
      setError('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerifications();
  }, [statusFilter]);

  const handleReview = async (id, action, reason = null) => {
    setActionLoading(id);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/admin/verifications/${id}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, rejection_reason: reason })
      });
      
      const data = await response.json();
      if (data.success) {
        // Remove from list or update status
        setVerifications(prev => prev.filter(v => v.id !== id));
        setStats(prev => ({
          ...prev,
          pending_count: Math.max(0, prev.pending_count - 1),
          approved_today: action === 'approve' ? prev.approved_today + 1 : prev.approved_today
        }));
      }
    } catch (err) {
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.length === 0) return;
    
    setActionLoading('bulk');
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/admin/verifications/bulk-review?action=${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedItems)
      });
      
      const data = await response.json();
      if (data.approved > 0 || data.rejected > 0) {
        setVerifications(prev => prev.filter(v => !selectedItems.includes(v.id)));
        setSelectedItems([]);
        fetchVerifications();
      }
    } catch (err) {
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === verifications.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(verifications.map(v => v.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const getProofPreview = (item) => {
    if (item.proof_screenshot) {
      return { type: 'image', content: item.proof_screenshot };
    }
    if (item.proof_url) {
      return { type: 'link', content: item.proof_url };
    }
    if (item.proof_text) {
      return { type: 'text', content: item.proof_text };
    }
    return null;
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    completed: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/20">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-400">{stats.pending_count}</p>
            <p className="text-xs text-white/50">Pending Review</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/20">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-400">{stats.approved_today}</p>
            <p className="text-xs text-white/50">Approved Today</p>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 text-center">
            <Image className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{verifications.filter(v => v.proof_screenshot).length}</p>
            <p className="text-xs text-white/50">With Screenshots</p>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 text-center">
            <Link2 className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{verifications.filter(v => v.proof_url).length}</p>
            <p className="text-xs text-white/50">With Links</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/50" />
          {['pending', 'completed', 'rejected', 'all'].map(status => (
            <Button
              key={status}
              size="sm"
              variant={statusFilter === status ? 'default' : 'outline'}
              className={statusFilter === status ? 'bg-[var(--burnz-orange)]' : 'border-white/10'}
              onClick={() => setStatusFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-white/10"
            onClick={fetchVerifications}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {selectedItems.length > 0 && (
            <>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleBulkAction('approve')}
                disabled={actionLoading === 'bulk'}
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Approve ({selectedItems.length})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleBulkAction('reject')}
                disabled={actionLoading === 'bulk'}
              >
                <X className="w-4 h-4 mr-1" />
                Reject ({selectedItems.length})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Verification List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--burnz-orange)]" />
        </div>
      ) : verifications.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-white/70">No verifications {statusFilter !== 'all' ? `with status "${statusFilter}"` : ''}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Select All */}
          <div className="flex items-center gap-2 px-4">
            <input
              type="checkbox"
              checked={selectedItems.length === verifications.length && verifications.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-white/20 bg-white/5"
            />
            <span className="text-sm text-white/50">Select All</span>
          </div>

          {/* Items */}
          <AnimatePresence>
            {verifications.map((item) => {
              const proof = getProofPreview(item);
              const isExpanded = expandedItem === item.id;
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card className={`bg-white/5 border-white/10 overflow-hidden ${
                    selectedItems.includes(item.id) ? 'ring-2 ring-[var(--burnz-orange)]' : ''
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="w-4 h-4 mt-1 rounded border-white/20 bg-white/5"
                        />

                        {/* Proof Preview Thumbnail */}
                        <div 
                          className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors flex-shrink-0"
                          onClick={() => proof && setPreviewModal(proof)}
                        >
                          {proof?.type === 'image' ? (
                            <img src={proof.content} alt="Proof" className="w-full h-full object-cover rounded-lg" />
                          ) : proof?.type === 'link' ? (
                            <Link2 className="w-6 h-6 text-blue-400" />
                          ) : (
                            <Eye className="w-6 h-6 text-white/30" />
                          )}
                        </div>

                        {/* Main Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white truncate">{item.task_title}</h4>
                            <Badge className={statusColors[item.status]}>
                              {item.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-white/50 truncate">{item.quest_title}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                            <span>User: {item.user_wallet}</span>
                            <span>Level: {item.user_level}</span>
                            <span>XP: +{item.xp_reward}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        {item.status === 'pending' && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleReview(item.id, 'approve')}
                              disabled={actionLoading === item.id}
                            >
                              {actionLoading === item.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReview(item.id, 'reject')}
                              disabled={actionLoading === item.id}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-4 pt-4 border-t border-white/10"
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-white/50 mb-1">Submitted At</p>
                              <p className="text-white">{new Date(item.submitted_at).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-white/50 mb-1">Quest Type</p>
                              <p className="text-white capitalize">{item.quest_type}</p>
                            </div>
                            {item.proof_url && (
                              <div className="col-span-2">
                                <p className="text-white/50 mb-1">Proof URL</p>
                                <a href={item.proof_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline break-all">
                                  {item.proof_url}
                                </a>
                              </div>
                            )}
                            {item.proof_text && (
                              <div className="col-span-2">
                                <p className="text-white/50 mb-1">Proof Text</p>
                                <p className="text-white bg-white/5 p-2 rounded">{item.proof_text}</p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewModal} onOpenChange={() => setPreviewModal(null)}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Proof Preview</DialogTitle>
          </DialogHeader>
          {previewModal?.type === 'image' && (
            <img src={previewModal.content} alt="Proof" className="w-full rounded-lg" />
          )}
          {previewModal?.type === 'link' && (
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-white/50 mb-2">Link submitted as proof:</p>
              <a href={previewModal.content} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline break-all">
                {previewModal.content}
              </a>
            </div>
          )}
          {previewModal?.type === 'text' && (
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-white">{previewModal.content}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificationQueue;
